import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { inr } from "@/lib/utils-format";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { AlertTriangle, DollarSign, PieChart, Bell, Zap, Trophy, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const SEV_STYLES = {
  critical: "bg-red-100 text-red-800 border-red-300",
  warning:  "bg-amber-100 text-amber-800 border-amber-300",
  info:     "bg-blue-100 text-blue-800 border-blue-300",
};

export default function BIDashboardPage() {
  const [d, setD] = useState(null);
  const [branches, setBranches] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    try {
      const [k, b, a] = await Promise.all([
        api.get("/bi/executive-kpis"),
        api.get("/bi/branch-profitability"),
        api.get("/bi/alerts"),
      ]);
      setD(k.data); setBranches(b.data); setAlerts(a.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load dashboard"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/bi/run-alert-scan");
      toast.success(`Scanned ${data.scanned} alerts — ${data.notifications_created} new notifications`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  if (!d) return <div className="text-[#8B7355] p-6">Loading executive dashboard…</div>;

  const k = d.current_month_kpis;
  const cf = d.cash_flow_forecast;

  return (
    <div className="p-6 space-y-6" data-testid="bi-dashboard">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <h1 className="font-[Outfit] text-3xl font-semibold text-slate-900">Business Intelligence</h1>
          <div className="text-xs text-[#8B7355]">Live KPIs for {d.current_month} · auto-refreshes every 60s</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {alerts?.critical > 0 && (
            <Badge className="bg-red-600 text-white text-xs" data-testid="bi-critical-badge">
              <AlertTriangle className="w-3 h-3 mr-1" />{alerts.critical} critical
            </Badge>
          )}
          <Button onClick={runScan} disabled={scanning} data-testid="bi-run-scan"
                  className="bg-[#C05621] hover:bg-[#9C4221] text-white">
            <Zap className="w-4 h-4 mr-1" />{scanning ? "Scanning…" : "Run Alert Scan"}
          </Button>
        </div>
      </div>

      {/* Top KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-revenue">
          <div className="uc-label flex items-center gap-2"><ArrowUpRight className="w-3 h-3 text-emerald-600" /> Revenue (MTD)</div>
          <div className="font-[Outfit] text-3xl font-semibold text-emerald-700 mt-1 tracking-tight">{inr(k.revenue)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Taxable value this month</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-cost">
          <div className="uc-label flex items-center gap-2"><ArrowDownLeft className="w-3 h-3 text-red-500" /> Cost (MTD)</div>
          <div className="font-[Outfit] text-3xl font-semibold text-red-700 mt-1">{inr(k.cost)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Expenses + purchases</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-net-profit">
          <div className="uc-label flex items-center gap-2"><DollarSign className="w-3 h-3" /> Net Profit (MTD)</div>
          <div className={`font-[Outfit] text-3xl font-semibold mt-1 ${k.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{inr(k.net_profit)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Revenue − Cost</div>
        </div>
        <div className="bg-[#FEEBC8] border-2 border-[#C05621] rounded-lg p-5" data-testid="kpi-margin">
          <div className="uc-label text-[#7B341E] flex items-center gap-2"><PieChart className="w-3 h-3" /> Net Margin</div>
          <div className={`font-[Outfit] text-4xl font-bold mt-1 ${k.margin_pct >= 0 ? "text-[#7B341E]" : "text-red-700"}`}>{k.margin_pct}%</div>
          <div className="text-[10px] text-[#7B341E] mt-1 opacity-80">Profit as % of revenue</div>
        </div>
      </div>

      {/* Revenue vs Expenses Chart */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="bi-chart">
        <div className="uc-label mb-2">Revenue vs Cost — 12 months</div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={d.monthly_series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8B7355" }} />
              <YAxis tick={{ fontSize: 11, fill: "#8B7355" }}
                     tickFormatter={(v) => v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : v} />
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ background: "white", border: "1px solid #EAE5D9" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#059669" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#DC2626" radius={[4, 4, 0, 0]} />
              <Bar dataKey="purchases" name="Purchases" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cash Flow Forecast + Top 5 SKUs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="bi-forecast">
          <div className="uc-label mb-3">30-day Cash Flow Forecast</div>
          <div className="space-y-2 text-sm">
            <Row lab="Cash In Hand" val={inr(cf.cash_in_hand)} accent={cf.cash_in_hand < 0 ? "text-red-700" : ""} />
            <Row lab="Bank balance" val={inr(cf.bank_balance)} />
            <Row lab="UPI / Wallets" val={inr(cf.upi_wallets)} />
            <div className="border-t border-[#EAE5D9] my-2"></div>
            <Row lab={<b>Current liquidity</b>} val={<b className={cf.current_liquidity < 0 ? "text-red-700" : ""}>{inr(cf.current_liquidity)}</b>} />
            <Row lab={<span className="text-emerald-700">+ Expected receipts (open invoices)</span>} val={<span className="text-emerald-700">{inr(cf.expected_receipts_30d)}</span>} />
            <Row lab={<span className="text-red-700">− Expected outflows (pending)</span>} val={<span className="text-red-700">{inr(cf.expected_outflows_30d)}</span>} />
            <div className="border-t-2 border-[#2C1810] my-2"></div>
            <div className="flex justify-between items-center bg-[#FEEBC8] rounded-md p-3">
              <span className="font-semibold text-[#7B341E]">Projected balance in 30 days</span>
              <span className={`font-[Outfit] text-xl font-bold ${cf.projected_balance_30d >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="bi-projected-balance">
                {inr(cf.projected_balance_30d)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="bi-top-skus">
          <div className="uc-label mb-3 flex items-center gap-2"><Trophy className="w-3 h-3 text-[#C05621]" /> Top 5 Selling SKUs (this month)</div>
          {(d.top_5_skus || []).length === 0 ? (
            <div className="text-sm text-[#8B7355]">No sales this month.</div>
          ) : (
            <div className="space-y-2">
              {d.top_5_skus.map((t, i) => (
                <div key={t.sku} className="flex items-center gap-3 p-2 bg-[#FAF6EE] rounded-md" data-testid={`bi-sku-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-[#C05621] text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-[10px] text-[#8B7355] font-mono">{t.sku} · {t.quantity} sold</div>
                  </div>
                  <div className="font-semibold text-emerald-700">{inr(t.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden" data-testid="bi-alerts">
        <div className="px-4 py-3 uc-label bg-[#FAF6EE] border-b border-[#EAE5D9] flex items-center gap-2">
          <Bell className="w-3 h-3 text-[#C05621]" /> Active alerts · {alerts?.count ?? 0}
          {alerts?.critical > 0 && <Badge className="bg-red-600 text-white text-[10px] ml-2">{alerts.critical} critical</Badge>}
          {alerts?.warning > 0 && <Badge className="bg-amber-500 text-white text-[10px]">{alerts.warning} warning</Badge>}
        </div>
        {(alerts?.alerts || []).length === 0 ? (
          <div className="p-6 text-center text-[#8B7355] text-sm">All clear — no active alerts.</div>
        ) : (
          <div className="max-h-[300px] overflow-y-auto">
            {alerts.alerts.slice(0, 15).map((a, i) => (
              <div key={i} className="px-4 py-2 border-b border-[#EAE5D9] last:border-0 flex items-center gap-3 text-sm" data-testid={`bi-alert-${i}`}>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${SEV_STYLES[a.severity]}`}>
                  {a.severity.toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{a.title}</div>
                  <div className="text-[10px] text-[#8B7355] truncate">{a.message}</div>
                </div>
                {a.amount !== undefined && (
                  <span className={`font-semibold text-xs ${(a.amount < 0) ? "text-red-700" : ""}`}>{inr(a.amount)}</span>
                )}
              </div>
            ))}
            {alerts.alerts.length > 15 && (
              <div className="p-3 text-center text-xs text-[#8B7355]">+{alerts.alerts.length - 15} more alerts…</div>
            )}
          </div>
        )}
      </div>

      {/* Branch Profitability */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto" data-testid="bi-branches">
        <div className="px-4 py-3 uc-label bg-[#FAF6EE] border-b border-[#EAE5D9]">Branch-wise profitability · {branches?.totals?.branches ?? 0} branches</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Branch</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Revenue</th>
              <th className="px-4 py-3 text-right">Product cost</th>
              <th className="px-4 py-3 text-right">Gross Profit</th>
              <th className="px-4 py-3 text-right">Expenses</th>
              <th className="px-4 py-3 text-right">Net Profit</th>
              <th className="px-4 py-3 text-right">Margin</th>
              <th className="px-4 py-3 text-right">Invoices</th>
            </tr>
          </thead>
          <tbody>
            {(branches?.rows || []).map((r) => (
              <tr key={r.branch_id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`bi-branch-${r.branch_id}`}>
                <td className="px-4 py-2 font-medium">{r.branch_name} <span className="text-[10px] text-[#8B7355] font-mono">{r.branch_code}</span></td>
                <td className="px-4 py-2"><Badge variant="outline" className="capitalize text-[10px]">{(r.branch_type || "").replace("_", " ")}</Badge></td>
                <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.revenue)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-red-700">{inr(r.product_cost)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.gross_profit)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs text-red-700">{inr(r.expenses)}</td>
                <td className={`px-4 py-2 text-right font-semibold ${r.net_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{inr(r.net_profit)}</td>
                <td className="px-4 py-2 text-right">
                  <Badge variant="outline" className={r.margin_pct >= 20 ? "border-emerald-500 text-emerald-700" : r.margin_pct >= 0 ? "border-amber-500 text-amber-700" : "border-red-500 text-red-700"}>
                    {r.margin_pct}%
                  </Badge>
                </td>
                <td className="px-4 py-2 text-right text-xs text-[#8B7355]">{r.invoice_count}</td>
              </tr>
            ))}
            {(branches?.rows || []).length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-[#8B7355]">No branches configured</td></tr>
            )}
          </tbody>
        </table>
        {branches?.totals?.best_branch && (
          <div className="px-4 py-2 text-xs text-[#8B7355] border-t border-[#EAE5D9]">
            Best branch: <b className="text-emerald-700">{branches.totals.best_branch}</b>
            {branches.totals.worst_branch && branches.totals.worst_branch !== branches.totals.best_branch && (
              <span> · Needs attention: <b className="text-red-700">{branches.totals.worst_branch}</b></span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ lab, val, accent }) => (
  <div className="flex justify-between items-center">
    <span className="text-[#5C4033]">{lab}</span>
    <span className={`font-mono ${accent || ""}`}>{val}</span>
  </div>
);
